import {
  type MantineThemeOverride,
  MantineProvider as Provider,
  createTheme,
  type MantineThemeColorsOverride,
} from '@mantine/core';
import {
  Global,
  MantineEmotionProvider,
  emotionTransform,
} from '@mantine/emotion';
import { Notifications } from '@mantine/notifications';
import { type ReactNode, useMemo } from 'react';
import { useV2UI } from '../hooks';
import { theme as defaultTheme } from './theme';

// import inter font
import '@fontsource/inter/200.css';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import '@fontsource/inter/900.css';

// import mantine styles
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css'; //if using mantine date picker
import '@mantine/notifications/styles.css';

type Colors = MantineThemeColorsOverride['colors'];

export type ThemeColor = keyof Colors;

export const RootThemeProvider = ({
  children,
  themePrimaryColor,
  colorScheme,
}: {
  children: ReactNode;
  themePrimaryColor: keyof Colors;
  colorScheme?: 'light' | 'dark';
}) => {
  const { isDarkTheme } = useV2UI();

  const theme = useMemo<MantineThemeOverride>(() => {
    return createTheme({
      ...defaultTheme,
      primaryColor: themePrimaryColor || defaultTheme.primaryColor,
    });
  }, [themePrimaryColor]);

  const forceColorScheme = useMemo(() => {
    if (colorScheme) return colorScheme;
    return isDarkTheme ? 'dark' : 'light';
  }, [colorScheme, isDarkTheme]);

  return (
    <Provider
      theme={theme}
      forceColorScheme={forceColorScheme}
      stylesTransform={emotionTransform}
      withGlobalClasses
    >
      <MantineEmotionProvider>
        {children}
        <Global
          styles={{
            '#sentry-feedback': {
              zIndex: 100,
            },
            'tfoot tr': {
              borderBottom: 'none!important',
            },
          }}
        />
        <Notifications
          sx={(theme) => ({
            '> div': {
              display: 'flex',
              gap: theme.spacing.xs,
              flexDirection: 'column',
            },
          })}
        />
      </MantineEmotionProvider>
    </Provider>
  );
};
